import React from 'react';
import { useAmorliaDashboard } from '../context/AmorliaDashboardContext';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/icons';

export default function HomePage({ setPage }) {
  const { extractQueue } = useAmorliaDashboard();
  const { user, profile } = useAuth();

  const catalog = (() => { try { return JSON.parse(localStorage.getItem('amorlia_catalog') || '[]'); } catch { return []; } })();
  const orders = (() => { try { return JSON.parse(localStorage.getItem('amorlia_orders') || '[]'); } catch { return []; } })();
  const published = catalog.filter(c => c.catalogStatus === 'published').length;
  const drafts = catalog.filter(c => c.catalogStatus === 'draft').length;
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const totalRevenue = orders.reduce((s, o) => s + (parseFloat(o.price) || 0) * (parseInt(o.quantity) || 1), 0);

  const cards = [
    { label: 'Queue', value: extractQueue.length, icon: Icon.extract, color: '#FF6B35', page: 'extract' },
    { label: 'Published', value: published, icon: Icon.catalog, color: '#2ED573', page: 'catalog' },
    { label: 'Drafts', value: drafts, icon: Icon.edit, color: '#FFA502', page: 'catalog' },
    { label: 'Orders', value: orders.length, icon: Icon.orders, color: '#3B82F6', page: 'orders' },
  ];

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem', color: 'var(--color-text-primary)' }}>
          Welcome back{profile?.display_name ? `, ${profile.display_name}` : ''}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Here's your store overview.</p>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {cards.map(c => (
          <div key={c.label} onClick={() => setPage?.(c.page)} className="glass-card glass-card-hover" style={{ padding: '1.25rem', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: c.color, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>{c.label}</div>
              </div>
              <div style={{ padding: '0.5rem', background: `${c.color}15`, borderRadius: '0.5rem', color: c.color }}>{c.icon(20)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue card */}
      <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Total Revenue</div>
        <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#2ED573' }}>${totalRevenue.toFixed(2)}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{pendingOrders} pending fulfillment</div>
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text-primary)' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => setPage?.('extract')} style={{ padding: '0.625rem 1rem', background: 'linear-gradient(135deg, #FF6B35, #FF8A5C)', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {Icon.plus(16)} Import Products
          </button>
          <button onClick={() => setPage?.('extract')} style={{ padding: '0.625rem 1rem', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '0.5rem', color: 'var(--color-text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {Icon.extract(16)} View Queue ({extractQueue.length})
          </button>
          <button onClick={() => setPage?.('orders')} style={{ padding: '0.625rem 1rem', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '0.5rem', color: 'var(--color-text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {Icon.orders(16)} Process Orders
          </button>
        </div>
      </div>

      {/* Recent queue items */}
      {extractQueue.length > 0 && (
        <div>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text-primary)' }}>Recent Queue Items</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {extractQueue.slice(0, 5).map((item, i) => (
              <div key={item.id || i} className="glass-card" style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {item.images?.[0] ? (
                  <img src={item.images[0]} alt="" style={{ width: 36, height: 36, borderRadius: '0.25rem', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: '0.25rem', background: 'var(--color-bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: '0.7rem' }}>?</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || 'Untitled'}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{item._source || 'Nihao'} {item.price ? `$${item.price}` : ''}</div>
                </div>
                {item.slStatus && (
                  <span style={{ fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4, background: item.slStatus === 'READY_TO_REVIEW' ? 'rgba(46,213,115,0.15)' : 'rgba(255,165,2,0.15)', color: item.slStatus === 'READY_TO_REVIEW' ? '#2ED573' : '#FFA502', fontWeight: 600 }}>{item.slStatus === 'READY_TO_REVIEW' ? 'Ready' : 'Review'}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
